import { useState, useMemo } from "react";
import { Eye, Copy, Pencil, CheckCircle2, X } from "lucide-react";
import { __ } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SectionCard } from "@/components/ui/section-card";
import { formatDate, fillStatementTemplate, copyToClipboard } from "@/helpers/statement-helpers";
import { getStatementTemplate } from "@/constants/statement-constants";
import { toast } from "sonner";
import type { WidgetConfig } from "@/types/widget";

interface StatementGeneratedProps {
  generatedDate: string;
  formData: WidgetConfig["modules"]["statement"]["formData"];
  displayInWidget: boolean;
  onEdit: () => void;
  onToggleDisplay: (checked: boolean) => void;
}

export function StatementGenerated({
  generatedDate,
  formData,
  displayInWidget,
  onEdit,
  onToggleDisplay,
}: StatementGeneratedProps) {
  const [previewOpen, setPreviewOpen] = useState(false);

  const statementHtml = useMemo(
    () => fillStatementTemplate(
      getStatementTemplate(),
      {
        companyName: formData?.companyName ?? "",
        businessEmail: formData?.businessEmail ?? "",
        website: formData?.website ?? "",
      },
      {
        wcagStandard: formData?.wcagStandard ?? "",
        conformanceStatus: formData?.conformanceStatus ?? "",
      },
      generatedDate,
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formData?.companyName,
      formData?.businessEmail,
      formData?.website,
      formData?.wcagStandard,
      formData?.conformanceStatus,
      generatedDate,
    ],
  );

  const handleCopy = async () => {
    try {
      await copyToClipboard(statementHtml);
      toast.success( __( 'Statement copied to clipboard.', 'accessibility-widget' ) );
    } catch {
      toast.error( __( 'Could not copy to clipboard.', 'accessibility-widget' ) );
    }
  };

  return (
    <>
      {/* Outer card: Grey style/Background = #F4F5FA */}
      <SectionCard className="cy:bg-[#F4F5FA]">
        <div className="cy:flex cy:items-start cy:justify-between cy:gap-4 cy:flex-wrap">
          <div className="cy:flex cy:items-center cy:gap-3">
            <CheckCircle2 className="cy:w-12 cy:h-12 cy:text-green-500 cy:flex-shrink-0" strokeWidth={1} />
            <div className="cy:space-y-0.5">
              <h3 className="cy:text-[18px] cy:font-semibold cy:text-foreground">
                {__( 'Statement generated', 'accessibility-widget' )}
              </h3>
              {generatedDate && (
                <p className="cy:text-sm cy:text-muted-foreground">
                  {__( 'Last updated:', 'accessibility-widget' )} {formatDate(generatedDate)}
                </p>
              )}
            </div>
          </div>

          <div className="cy:flex cy:items-center cy:gap-2 cy:flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className="cy:gap-1.5 cy:rounded cy:border-primary! cy:bg-white cy:text-primary! cy:text-sm"
              onClick={() => setPreviewOpen(true)}
            >
              <Eye className="cy:w-4 cy:h-4" aria-hidden="true" />
              {__( 'Preview', 'accessibility-widget' )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="cy:gap-1.5 cy:rounded cy:border-primary! cy:bg-white cy:text-primary! cy:text-sm"
              onClick={handleCopy}
            >
              <Copy className="cy:w-4 cy:h-4" aria-hidden="true" />
              {__( 'Copy statement', 'accessibility-widget' )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="cy:gap-1.5 cy:rounded cy:border-primary! cy:bg-white cy:text-primary! cy:text-sm"
              onClick={onEdit}
            >
              <Pencil className="cy:w-4 cy:h-4" aria-hidden="true" />
              {__( 'Edit', 'accessibility-widget' )}
            </Button>
          </div>
        </div>

        {/* Display in widget overlay: fill_B0QQJR = #D8E8FF */}
        <div className="cy:mt-4 cy:rounded-lg cy:bg-[#D8E8FF] cy:p-4">
          <div className="cy:flex cy:items-start cy:justify-between cy:gap-4">
            <div className="cy:space-y-0.5">
              <label
                htmlFor="cy-display-in-widget"
                className="cy:block cy:text-base cy:font-semibold cy:text-foreground cy:cursor-pointer"
              >
                {__( 'Display in widget overlay', 'accessibility-widget' )}
              </label>
              <span className="cy:block cy:text-sm cy:text-muted-foreground">
                {__( 'Show this generated statement directly inside the AccessiYes widget.', 'accessibility-widget' )}
              </span>
            </div>
            <Switch
              id="cy-display-in-widget"
              checked={displayInWidget}
              onCheckedChange={onToggleDisplay}
            />
          </div>
        </div>
      </SectionCard>

      {/* Preview modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent showCloseButton={false} className="cy:max-w-2xl cy:max-h-[85vh] cy:flex cy:flex-col cy:gap-0 cy:p-0">
          <DialogHeader className="cy:flex cy:flex-row cy:items-center cy:justify-between cy:px-6 cy:py-4 cy:border-b cy:border-gray-200">
            <DialogTitle className="cy:text-base cy:font-semibold">{__( 'Preview', 'accessibility-widget' )}</DialogTitle>
            <div className="cy:flex cy:items-center cy:gap-2">
              <Button
                variant="outline"
                size="sm"
                className="cy:gap-1.5 cy:rounded cy:border-primary! cy:text-primary! cy:text-sm"
                onClick={handleCopy}
              >
                <Copy className="cy:w-4 cy:h-4" aria-hidden="true" />
                {__( 'Copy statement', 'accessibility-widget' )}
              </Button>
              <button
                onClick={() => setPreviewOpen(false)}
                className="cy:rounded cy:p-1 cy:text-muted-foreground cy:hover:text-foreground cy:hover:bg-gray-100 cy:transition-colors"
                aria-label={__( 'Close preview', 'accessibility-widget' )}
              >
                <X className="cy:w-5 cy:h-5" />
              </button>
            </div>
          </DialogHeader>
          <div
            className="cy:overflow-y-auto cy:px-8 cy:py-6 cy:prose cy:prose-sm cy:max-w-none"
            role="document"
            aria-label={__( 'Accessibility statement content', 'accessibility-widget' )}
            tabIndex={0}
            dangerouslySetInnerHTML={{ __html: statementHtml }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
